import { GlAccountTypeOption } from '@/utils/constants';
import MDinput from '@/components/MDinput';
import { validatorRequire, validatorRequireNumber, validatorConditionalRequired } from '@/utils/validators';
import Switch from '@/components/DSE/Switch';
import Select from '@/components/DSE/Select';
import InputNumber from '@/components/DSE/InputNumber';
import PriceListItemPricesSummary from './pricelistitemprices/Summary';
import CultureMatrix from './pricelistmatrices/cultureMatrix';
import CountryMatrix from './pricelistmatrices/countryMatrix';

export function getFormDefinition(vue) {
  return {
    type: 'tab',
    saveButtons: [true], // Page, tab 1, tab 2, ...
    showFormLabels: [true, false, false, false], // Tab 1, tab 2, ....
    labels: [
      'priceListItem.tabGeneral',
      'priceListItem.tabPrices',
      'priceListItem.tabMatrix',
      'priceListItem.tabCountryMatrix'
    ],
    headerTemplate: (str, form) => `
    <h3>
    ${form.productCode + ' - ' + form.description || ''}
    </h3>
    `,
    groups: [
      {
        productCode: {
          type: MDinput,
          defaultValue: '',
          props: {
            caption: 'priceListItem.productCode',
            maxlength: 64,
            required: true
          },
          rules: [{ validator: validatorRequire }]
        },
        description: {
          type: MDinput,
          defaultValue: '',
          props: {
            maxlength: 64,
            required: true
          },
          rules: [{ validator: validatorRequire }]
        },
        descriptionInvoice: {
          type: MDinput,
          defaultValue: '',
          props: {
            caption: 'priceListItem.descriptionInvoice',
            maxlength: 64,
            required: true
          },
          rules: [{ validator: validatorRequire }]
        },
        descriptionInvoiceForeign: {
          type: MDinput,
          defaultValue: '',
          props: {
            caption: 'priceListItem.descriptionInvoiceForeign',
            maxlength: 64,
            required: true
          },
          rules: [{ validator: validatorRequire }]
        },
        gtinNumber: {
          type: MDinput,
          defaultValue: null,
          props: {
            caption: 'priceListItem.gtinNumber',
            maxlength: 13
          }
        },
        unitOption: {
          type: Select,
          defaultValue: '',
          props: {
            caption: 'priceListItem.unit',
            required: true,
            valueField: 'code',
            labelField: 'description',
            loading: vue.$store.getters['option/isLoading'],
            options: vue.$store.getters['option/loadedOptions']('capacityUnit')
          },
          rules: [{ validator: validatorRequireNumber }]
        },
        markUpYN: {
          type: Switch,
          defaultValue: false,
          props: {
            caption: 'priceListItem.markUpYN',
            activeText: 'common.yes',
            inactiveText: 'common.no'
          }
        },
        markUpType: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'priceListItem.markUpType',
            valueField: 'code',
            labelField: 'description',
            loading: vue.$store.getters['option/isLoading'],
            options: vue.$store.getters['option/loadedOptions']('markUpType')
          },
          dynamicProps: form => ({
            visible: form.markUpYN
          })
        },
        matrixYN: {
          type: Switch,
          defaultValue: false,
          props: {
            caption: 'priceListItem.matrixYN',
            activeText: 'common.yes',
            inactiveText: 'common.no'
          },
          dynamicProps: form => ({
            readonly: vue.isEdit
          })
        },
        externalOnlyYN: {
          type: Switch,
          defaultValue: false,
          props: {
            caption: 'priceListItem.externalOnlyYN',
            activeText: 'common.no',
            inactiveText: 'common.yes'
          }
        },
        glAccountID: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'common.clientSales',
            valueField: 'id',
            labelField: 'descriptionEx',
            loading: vue.$store.getters['glAccount/isLoading'],
            options: vue.$store.getters['glAccount/loadedItems'].filter(x => x.type === GlAccountTypeOption.ClientSales)
          }
        },
        intercompanySalesGlAccounts: {
          type: Select,
          defaultValue: [],
          props: {
            caption: 'common.intercompanySales',
            valueField: 'id',
            labelField: 'descriptionEx',
            multiple: true,
            loading: vue.$store.getters['glAccount/isLoading'],
            options: vue.$store.getters['glAccount/loadedItems'].filter(
              x => x.type === GlAccountTypeOption.IntercompanySales
            )
          }
        },
        orderLinePriceType: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'orderLine.orderLinePriceType',
            valueField: 'code',
            labelField: 'description',
            loading: vue.$store.getters['option/isLoading'],
            options: vue.$store.getters['option/loadedOptions']('orderLinePriceType'),
            required: true
          },
          rules: [{ validator: validatorRequireNumber }]
        },
        minimumWidth: {
          type: InputNumber,
          defaultValue: 0,
          valueOnFormChange: (form, oldValue) => {
            if (!form.matrixYN) {
              return 0;
            }
            return oldValue;
          },
          props: {
            caption: 'priceListItem.minimumWidth'
          },
          dynamicProps: form => ({
            visible: form.matrixYN,
            disabled: vue.isEdit
          }),
          dynamicRules: form => [{ validator: validatorConditionalRequired(form.matrixYN) }]
        },
        maximumWidth: {
          type: InputNumber,
          defaultValue: 0,
          valueOnFormChange: (form, oldValue) => {
            if (!form.matrixYN) {
              return 0;
            }
            return oldValue;
          },
          props: {
            caption: 'priceListItem.maximumWidth'
          },
          dynamicProps: form => ({
            visible: form.matrixYN,
            disabled: vue.isEdit
          }),
          dynamicRules: form => [{ validator: validatorConditionalRequired(form.matrixYN) }]
        },
        stepSizeWidth: {
          type: InputNumber,
          defaultValue: 0,
          valueOnFormChange: (form, oldValue) => {
            if (!form.matrixYN) {
              return 0;
            }
            return oldValue;
          },
          props: {
            caption: 'priceListItem.stepSizeWidth'
          },
          dynamicProps: form => ({
            visible: form.matrixYN,
            disabled: vue.isEdit
          }),
          dynamicRules: form => [{ validator: validatorConditionalRequired(form.matrixYN) }]
        },
        minimumLength: {
          type: InputNumber,
          defaultValue: 0,
          valueOnFormChange: (form, oldValue) => {
            if (!form.matrixYN) {
              return 0;
            }
            return oldValue;
          },
          props: {
            caption: 'priceListItem.minimumLength'
          },
          dynamicProps: form => ({
            visible: form.matrixYN,
            disabled: vue.isEdit
          }),
          dynamicRules: form => [{ validator: validatorConditionalRequired(form.matrixYN) }]
        },
        maximumLength: {
          type: InputNumber,
          defaultValue: 0,
          valueOnFormChange: (form, oldValue) => {
            if (!form.matrixYN) {
              return 0;
            }
            return oldValue;
          },
          props: {
            caption: 'priceListItem.maximumLength'
          },
          dynamicProps: form => ({
            visible: form.matrixYN,
            disabled: vue.isEdit
          }),
          dynamicRules: form => [{ validator: validatorConditionalRequired(form.matrixYN) }]
        },
        stepSizeLength: {
          type: InputNumber,
          defaultValue: 0,
          valueOnFormChange: (form, oldValue) => {
            if (!form.matrixYN) {
              return 0;
            }
            return oldValue;
          },
          props: {
            caption: 'priceListItem.stepSizeLength'
          },
          dynamicProps: form => ({
            visible: form.matrixYN,
            disabled: vue.isEdit
          }),
          dynamicRules: form => [{ validator: validatorConditionalRequired(form.matrixYN) }]
        }
      },
      {
        pricesSummary: {
          type: PriceListItemPricesSummary,
          props: {
            caption: 'common.emptyString'
          },
          dynamicProps: form => ({
            widths: form.widths,
            heights: form.heights
          })
        }
      },
      {
        matrixEntries: {
          type: CultureMatrix,
          defaultValue: [],
          props: {
            caption: 'common.emptyString'
          }
        }
      },
      {
        countryMatrixEntries: {
          type: CountryMatrix,
          defaultValue: [],
          props: {
            caption: 'common.emptyString'
          }
        }
      }
    ]
  };
}

const layoutTabGeneral = {
  named: true,
  sections: [
    {
      width: 50,
      fields: [
        'productCode',
        'description',
        'descriptionInvoice',
        'descriptionInvoiceForeign',
        'gtinNumber',
        'unitOption',
        'markUpYN',
        'markUpType',
        'matrixYN',
        'externalOnlyYN',
        'glAccountID',
        'intercompanySalesGlAccounts',
        'orderLinePriceType'
      ]
    },
    {
      width: 50,
      fields: ['minimumWidth', 'maximumWidth', 'stepSizeWidth', 'minimumLength', 'maximumLength', 'stepSizeLength']
    }
  ]
};

const layoutFullWidth = {
  sections: [
    {
      width: 100
    }
  ]
};

export function getCreateLayoutDefinition(vue) {
  return {
    groups: [layoutTabGeneral]
  };
}

export function getEditLayoutDefinition(vue) {
  if (vue.form.matrixYN) {
    return { groups: [layoutTabGeneral, layoutFullWidth, layoutFullWidth, layoutFullWidth] };
  }
  return { groups: [layoutTabGeneral, layoutFullWidth] };
}

export function createItemFromFormData(vue, form) {
  return form;
}

export function createFormDataFromItem(vue, item) {
  const form = {
    ...item
  };
  return form;
}
